import { useState, useRef, useEffect } from "react";
import { Send, Loader2, Sparkles, Bot, User } from "lucide-react";
import { toast } from "sonner";
import { travelApi } from "@/lib/travelApi";

const SUGGESTED = [
  "¿Qué comida típica debería probar?",
  "¿Cuáles son las costumbres locales que debo respetar?",
  "Cuéntame una historia o leyenda del lugar",
  "¿Qué llevar en la mochila para este destino?",
];

export default function CulturalAssistant({ place }) {
  const [messages, setMessages] = useState([]); // {role, content}
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const scrollerRef = useRef(null);

  useEffect(() => {
    // Scroll to bottom whenever messages change
    if (scrollerRef.current) {
      scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
    }
  }, [messages, loading]);

  const ask = async (questionText) => {
    const q = (questionText ?? input).trim();
    if (!q || loading) return;
    setMessages((m) => [...m, { role: "user", content: q }]);
    setInput("");
    setLoading(true);
    try {
      const res = await travelApi.askCultural(place.id, q, sessionId);
      setSessionId(res.session_id);
      setMessages((m) => [...m, { role: "assistant", content: res.answer || "(sin respuesta)" }]);
    } catch (err) {
      const msg = err?.response?.data?.detail || "El asistente no pudo responder ahora.";
      toast.error(msg);
      setMessages((m) => [...m, { role: "assistant", content: `⚠️ ${msg}`, error: true }]);
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = (e) => {
    e.preventDefault();
    ask();
  };

  return (
    <section data-testid="cultural-assistant" className="bg-gradient-to-br from-coral/10 via-cream-card to-cream rounded-2xl border hairline overflow-hidden">
      <header className="flex items-center gap-2 px-4 py-3 border-b hairline bg-cream-card/50">
        <div className="w-8 h-8 rounded-full bg-coral/15 grid place-items-center">
          <Sparkles className="w-4 h-4 text-coral" />
        </div>
        <div>
          <p className="text-sm font-medium leading-tight">Pregúntale al guía cultural</p>
          <p className="text-[11px] font-mono text-ink-mid">IA con contexto histórico de {place.name}</p>
        </div>
      </header>

      <div ref={scrollerRef} className="max-h-72 overflow-y-auto px-4 py-3 space-y-3">
        {messages.length === 0 && (
          <div data-testid="cultural-suggestions" className="space-y-2">
            <p className="text-[12px] text-ink-mid">Prueba con una de estas preguntas:</p>
            <div className="flex flex-wrap gap-1.5">
              {SUGGESTED.map((s) => (
                <button
                  key={s}
                  onClick={() => ask(s)}
                  data-testid={`cultural-suggested-${s.slice(0, 20)}`}
                  className="text-[11px] bg-cream-card border hairline rounded-full px-3 py-1.5 hover:border-coral hover:text-coral transition"
                  disabled={loading}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} className={`flex gap-2 ${m.role === "user" ? "justify-end" : ""}`}>
            {m.role === "assistant" && (
              <div className="w-7 h-7 rounded-full bg-coral/15 grid place-items-center shrink-0">
                <Bot className="w-3.5 h-3.5 text-coral" />
              </div>
            )}
            <div
              className={`max-w-[78%] text-sm leading-relaxed whitespace-pre-line rounded-2xl px-3 py-2 ${
                m.role === "user"
                  ? "bg-ink text-cream rounded-br-sm"
                  : m.error
                  ? "bg-red-50 border hairline border-red-200 text-red-800"
                  : "bg-cream-card border hairline"
              }`}
            >
              {m.content}
            </div>
            {m.role === "user" && (
              <div className="w-7 h-7 rounded-full bg-ink/10 grid place-items-center shrink-0">
                <User className="w-3.5 h-3.5" />
              </div>
            )}
          </div>
        ))}
        {loading && (
          <div className="flex gap-2 items-center">
            <div className="w-7 h-7 rounded-full bg-coral/15 grid place-items-center shrink-0">
              <Bot className="w-3.5 h-3.5 text-coral" />
            </div>
            <div className="bg-cream-card border hairline rounded-2xl px-3 py-2 text-sm text-ink-mid inline-flex items-center gap-2">
              <Loader2 className="w-3.5 h-3.5 animate-spin" /> Pensando…
            </div>
          </div>
        )}
      </div>

      <form onSubmit={onSubmit} className="flex gap-2 p-3 border-t hairline bg-cream-card/30">
        <input
          data-testid="cultural-input"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder={`Pregunta algo sobre ${place.name}…`}
          maxLength={800}
          disabled={loading}
          className="flex-1 bg-cream border hairline rounded-full px-4 py-2 text-sm outline-none focus:border-coral disabled:opacity-60"
        />
        <button
          type="submit"
          data-testid="cultural-send"
          disabled={loading || !input.trim()}
          className="bg-ink text-cream rounded-full px-4 inline-flex items-center gap-1.5 hover:bg-coral transition disabled:opacity-50"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        </button>
      </form>
    </section>
  );
}
